import * as posActions from "../../lib/posActions";
import * as routeHelpers from "../../lib/routeHelpers";
import * as searchParamNames from "../../constants/searchParamNames";
import PaymentMethodsHeader from "../PaymentMethodsHeader";
import React, {useEffect, useRef, useState} from "react";
import styles from "./ExternalTender.module.css";
import CurrencyInput from "../CurrencyInput";
import TextButton from "../TextButton";
import {API, Lib} from "habit-core";
import {useNavigate, useSearchParams} from "react-router-dom";
import OutlineButton from "../OutlineButton";
import StyledKeyboard from "../StyledKeyboard";
import useOnChangeCashAmount from "../../lib/useOnChangeCashAmount";
import {useAppDispatch} from "../../lib/hooks";

const strings = {
    apply: "Apply",
    backPromptLabel: (tenderName: string) =>
        `Other Payment Options // ${tenderName}`,
    enterAmount: "Enter Amount",
    reset: "Reset",
};

export default function ExternalTender() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const tenderName = searchParams.get(searchParamNames.TENDER_NAME);

    const [appliedAmountCents, setAppliedAmountCents] =
        useState<API.models.USDCents | null>(null);
    const [displayedAppliedAmount, setDisplayedAppliedAmount] = useState("");

    const currencyInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const floatVal = parseFloat(displayedAppliedAmount);
        if (
            isNaN(floatVal) ||
            appliedAmountCents !== Lib.currency.dollarsFloatToCents(floatVal)
        ) {
            setDisplayedAppliedAmount(
                appliedAmountCents !== null
                    ? Lib.currency
                          .centsToDollarString(appliedAmountCents)
                          .replace("$", "")
                    : "",
            );
        }
    }, [appliedAmountCents]);

    const onChangeAppliedAmount = useOnChangeCashAmount(
        setDisplayedAppliedAmount,
        setAppliedAmountCents,
    );

    const onApply = () => {
        if (!tenderName || !appliedAmountCents) {
            return;
        }

        dispatch(
            posActions.applyExternalTender({
                tenderName: tenderName,
                appliedCents: appliedAmountCents,
            }),
        );
        navigate(routeHelpers.payment());
    };

    if (tenderName == null) {
        return null;
    }

    return (
        <div className={styles.container}>
            <PaymentMethodsHeader
                backPromptLabel={strings.backPromptLabel(tenderName)}
                to={routeHelpers.otherPaymentMethods()}
            />
            <div className={styles.contentContainer}>
                <div className={styles.tenderName}>{tenderName}</div>
                <div className={styles.buttonRow}>
                    <CurrencyInput
                        className={styles.currencyInput}
                        placeholder={strings.enterAmount}
                        value={displayedAppliedAmount}
                        onChange={setAppliedAmountCents}
                    />
                    <TextButton
                        className={styles.resetButton}
                        label={strings.reset}
                        onClick={() => {
                            setAppliedAmountCents(null);
                        }}
                    />
                </div>
                <div className={styles.keyboardContainer}>
                    <StyledKeyboard
                        className={styles.keyboard}
                        currentInput={displayedAppliedAmount}
                        alwaysVisible
                        onChange={onChangeAppliedAmount}
                        inputRefs={[currencyInputRef]}
                        layout="numeric-no-enter"
                    />
                </div>

                <OutlineButton
                    label={strings.apply}
                    onClick={onApply}
                    disabled={!appliedAmountCents || !tenderName}
                    className={styles.applyButton}
                    mode="blue"
                />
            </div>
        </div>
    );
}
